import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  FormControl,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import API_CONFIG from '../../config/api';
import { getApiUrl } from '../../config/api';
const questions = [
  {
    id: 'source',
    title: 'How did you hear about SurveySlack?',
    type: 'radio',
    options: [
      'A friend or colleague',
      'Social media (Facebook, Twitter, etc.)',
      'Online search',
      'Advertisement',
      'Other'
    ]
  },
  {
    id: 'role',
    title: 'Which best describes your role?',
    type: 'radio',
    options: [
      'Marketer',
      'Researcher',
      'HR/People Operations',
      'Educator',
      'Business Owner',
      'Other'
    ]
  },
  {
    id: 'goal',
    title: 'What is your primary goal for using SurveySlack?',
    type: 'radio',
    options: [
      'Gathering customer feedback',
      'Conducting market research',
      'Collecting employee insights',
      'Creating quick polls',
      'Other (please specify)'
    ]
  },
  {
    id: 'feature',
    title: 'Which feature are you most excited to try?',
    type: 'radio',
    options: [
      'Easy survey creation with drag-and-drop',
      'Custom themes and branding',
      'Conditional (skip) logic for dynamic surveys',
      'Built-in reporting and analytics',
      "I'm exploring—show me around!"
    ]
  },
  {
    id: 'suggestions',
    title: '(Optional) Any suggestions or ideas to make SurveySlack even better?',
    type: 'text'
  }
];

const Onboarding = () => {
  const [answers, setAnswers] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();
  const { logEvent } = useAnalytics();
  const location = useLocation();

  useEffect(() => {
    try {
      // Check for referral in cookies and URL
      const urlParams = new URLSearchParams(location.search);
      const urlRef = urlParams.get('ref');
      const cookieRef = Cookies.get('referral');
      
      // Set referral source in answers if found
      if (urlRef || cookieRef) {
        const referralCode = urlRef || cookieRef;
        setAnswers(prev => ({
          ...prev,
          source: 'Referral',
          referralCode: referralCode
        }));
        
        // Store referral code in cookie if it came from URL
        if (urlRef && !cookieRef) {
          Cookies.set('referral', urlRef, { expires: 30 }); // Store for 30 days
        }
      }
    } catch (error) {
      console.error('Error handling referral:', error);
    }
  }, [location]);

  const handleSkip = async () => {
    try {
      // Prepare the data with all questions marked as skipped
      const submissionData = {
        userId: user?.uid,
        answers: {
          source: answers.source || 'skipped',
          role: answers.role || 'skipped',
          goal: answers.goal || 'skipped',
          feature: answers.feature || 'skipped',
          suggestions: answers.suggestions || '',
          referralCode: answers.referralCode || null,
      
        }
      };
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ONBOARDINGRESPONSES.SUBMIT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to submit onboarding answers');
      }

      // Log analytics event for skipped onboarding
      logEvent('onboarding_skipped', {
        userId: user?.uid
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding skip error:', error);
      navigate('/dashboard');
    }
  };

  const handleSubmit = async () => {
    try {
      // Prepare the data, marking unanswered questions as 'skipped'
      const submissionData = {
        userId: user?.uid,
        answers: {
          source: answers.source || 'skipped',
          role: answers.role || 'skipped',
          goal: answers.goal || 'skipped',
          feature: answers.feature || 'skipped',
          suggestions: answers.suggestions || 'skipped',
          referralCode: answers.referralCode || null,
        }
      };

   

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ONBOARDINGRESPONSES.SUBMIT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to submit onboarding answers');
      }
      console.log(`apiUrl: ${getApiUrl(API_CONFIG.ENDPOINTS.ONBOARDINGRESPONSES.SUBMIT)}`);

      // Log analytics event
      logEvent('onboarding_complete', {
        userId: user?.uid,
        answers
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding submission error:', error);
      navigate('/dashboard');
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const canSubmit = questions.every(q => 
    q.type === 'text' || answers[q.id] || !q.required
  );

  return (
    <Container component="main" maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4 },
          width: '100%',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
        }}
      >
        <Typography component="h1" variant="h4" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
          Welcome to SurveySlack!
        </Typography>

        <Grid container spacing={4} sx={{ mb: 4 }}>
          {questions.map((question, index) => (
            <Grid item xs={12} md={6} key={question.id}>
              <Box sx={{ height: '100%', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {question.title}
                </Typography>

                {question.type === 'radio' ? (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel 
                      id={`${question.id}-label`}
                      sx={{
                        backgroundColor: 'background.paper',
                        px: 1
                      }}
                    >
                      Select an option
                    </InputLabel>
                    <Select
                      labelId={`${question.id}-label`}
                      value={answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      label="Select an option"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'grey.300'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      {question.options.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Share your thoughts..."
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    sx={{
                      '& .MuiInputBase-root': {
                        display: 'flex',
                        textAlign: 'center',
                        '& textarea': {
                          textAlign: 'center'
                        }
                      }
                    }}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleSkip}
            size="large"
          >
            Skip
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            size="large"
          >
            Next
          </Button>
        </Box>
        </Paper>
    </Container>
  );
};

export default Onboarding;